import type { DragEvent } from "react";
import type { Lead } from "@/types";
import { leadName, dueInfo, isSet } from "@/lib/leads";
import { formatDateTime } from "@/lib/format";

/** A single lead card within a column. Click opens the drawer. Draggable only
 *  when drag handlers are given — the Won column omits them, so a closed-won
 *  lead can't be dragged back into a pipeline stage. */
export function LeadCard({
  lead,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  lead: Lead;
  onOpen: () => void;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
}) {
  const due = dueInfo(lead.next_action_at);
  const attribution = [lead.source, lead.campaign].filter(Boolean).join(" · ");
  const showConsult = lead.stage === "scheduled" && isSet(lead.consultation_at);

  return (
    <article
      className="lead-card"
      draggable={Boolean(onDragStart)}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <p className="lead-card__name">{leadName(lead)}</p>
      {lead.project_description ? (
        <p className="lead-card__desc">{lead.project_description}</p>
      ) : null}

      {showConsult ? (
        <p className="lead-card__consult numeric">{formatDateTime(lead.consultation_at)}</p>
      ) : null}

      <div className="lead-card__meta">
        {isSet(lead.spam_at) ? <span className="lead-card__chip lead-card__chip--spam">Spam</span> : null}
        {lead.site_postcode ? <span className="lead-card__chip numeric">{lead.site_postcode}</span> : null}
        {lead.owner ? <span className="lead-card__owner">{lead.owner}</span> : null}
        {due ? <span className={`lead-card__due lead-card__due--${due.tone}`}>{due.label}</span> : null}
      </div>

      {attribution ? <p className="lead-card__attr">{attribution}</p> : null}
    </article>
  );
}
